<template>
  <cal-dialog-shell :initializing="loading || !institution || !institutionId" :value="value" cancel-text="Schließen" hide-save
                    icon="mdi-domain" title="" tone="info" @input="$emit('input', false)">
    <template #title-extra>
      <span v-if="!institution">Standort</span>
      <template v-else>
        <span v-can:read-institutions="{ id: institutionId, keys: ['name'] }" class="mr-1">{{ institution.name }}</span>
        <span v-if="institution.customId" v-can:read-institutions="{ id: institutionId, keys: ['customId'] }"
              class="tag tag--sm tone-muted">{{ institution.customId }}</span>
      </template>
    </template>

    <div v-if="institution" class="pcd-fields">
      <item-availabilities v-can:read-institutions="{ id: institutionId, keys: ['availabilities'] }" :institution="institution"/>

      <profile-field v-if="institution.address" v-can:read-institutions="{ id: institutionId, keys: ['address'] }"
                     icon="mdi-map-marker-outline" label="Adresse">
        <div class="fld__value">{{ institution.address.street }} {{ institution.address.houseNumber }}</div>
        <div class="fld__value">{{ institution.address.postal }} {{ institution.address.city }}</div>
        <template #actions>
          <a :href="mapsLink" aria-label="In Google Maps öffnen" class="ibtn" rel="noopener" target="_blank" v-tip="'In Google Maps öffnen'">
            <v-icon>mdi-map-search-outline</v-icon>
          </a>
          <button aria-label="Adresse kopieren" class="ibtn" type="button" v-tip="'Adresse kopieren'" @click="copy(addressText, 'Adresse')">
            <v-icon>mdi-content-copy</v-icon>
          </button>
        </template>
      </profile-field>

      <profile-field v-can:read-institutions="{ id: institutionId, keys: ['contactInformation'] }" icon="mdi-card-account-phone-outline"
                     label="Kontakt">
        <contact-lines :lines="contactLines"/>
      </profile-field>

      <profile-field v-can:read-institutions="{ id: institutionId, keys: ['responsibles'] }" icon="mdi-account-tie-outline"
                     label="Ansprechpartner">
        <div v-if="!responsibles.length" class="fld__value fld__value--muted">Es wurden noch keine Ansprechpartner angelegt.</div>
        <person-row v-for="item in responsibles" :key="String(item._id)" v-can:read-responsibles="{ id: item._id, keys: ['lastName', 'gender'] }"
                    :name="extractName(item)" :subtitle="canReadJobType(item) ? getJobTypeString(item) : ''">
          <template #actions>
            <button v-can:update-responsibles="{ id: item._id }" aria-label="Bearbeiten" class="ibtn" type="button" v-tip="'Bearbeiten'"
                    @click="activeResponsible = item; showEditResponsibleDialog = true">
              <v-icon>mdi-pencil-outline</v-icon>
            </button>
            <button aria-label="Ansehen" class="ibtn" type="button" v-tip="'Ansehen'"
                    @click="activeResponsible = item; showResponsibleDialog = true">
              <v-icon>mdi-eye-outline</v-icon>
            </button>
          </template>
        </person-row>
        <template v-if="!responsibles.length" #actions>
          <button aria-label="Ansprechpartner anlegen" class="ibtn ibtn--add" type="button" v-tip="'Ansprechpartner anlegen'"
                  @click="showCreateResponsibleDialog = true">
            <v-icon>mdi-plus</v-icon>
          </button>
        </template>
      </profile-field>

      <!-- Same API calls and guards as the former assigned/planned employees list items -->
      <!--suppress VueMissingComponentImportInspection -->
      <item-teachers :institution-id="institutionId"/>
    </div>

    <template #footer-start>
      <button v-can:custom-shareAccess class="cta cta--ghost" type="button" v-tip="'Ansprechpartner:in freigeben'" @click="shareAccess">
        <v-icon class="cta__icon" small>mdi-shield-lock-open</v-icon>Freigeben
      </button>
    </template>

    <!-- DIALOGS -->
    <create-responsible-dialog v-if="institution" v-model="showCreateResponsibleDialog" :institution-id="institution._id"
                               :institution-name="institution.name || ''"/>
    <edit-responsible-dialog v-if="activeResponsible" v-model="showEditResponsibleDialog" :responsible-id="activeResponsible._id"/>
    <responsible-card-dialog v-if="activeResponsible && institution" v-model="showResponsibleDialog" :institution-id="institution._id"
                             :responsible="activeResponsible"/>
  </cal-dialog-shell>
</template>


<script lang="ts">
import Vue, { Component } from 'vue';
import {CrudEntityTypes} from '@/classes/clientOnly/permissionTreeResources/enums/CrudEntityTypes';
import {CrudActionTypes} from '@/classes/clientOnly/permissionTreeResources/enums/CrudActionTypes';
import {GenderTypes} from '@/enums/GenderTypes.enum';
import {Institution} from '@/interfaces/institution.interface';
import {Responsible} from '@/interfaces/responsible.interface';
import {isJobType} from '@/interfaces/jobType.interface';
import {InstitutionerApi} from '@/classes/api/institutioner.api.class';
import {isObjectId} from '@/helpers/isObjectId.helper';
import {extractName} from '@/helpers/extractName';
import CalDialogShell from '@/components/timetable2/dialogs/CalDialogShell.vue';
import ProfileField from '@/components/common/profileUi/ProfileField.vue';
import ContactLines from '@/components/common/profileUi/ContactLines.vue';
import PersonRow from '@/components/common/profileUi/PersonRow.vue';
import {ContactLine, copyWithSnackbar} from '@/components/common/profileUi/profileUi.helper';
import ItemAvailabilities from '@/components/acquisitionList/ui/itemAvailabilities.vue';
import CreateResponsibleDialog from '@/components/responsibles/createResponsibleDialog.vue';
import EditResponsibleDialog from '@/components/responsibles/editResponsibleDialog.vue';
import ResponsibleCardDialog from '@/components/responsibles/responsibleCardDialog.vue';
import {addressTextOf, contactLinesOf, mapsLinkOf} from './cardDialog.helper';
import * as mongoose from "mongoose";

export default Vue.extend({
  components: {
    CalDialogShell, ProfileField, ContactLines, PersonRow, ItemAvailabilities,
    CreateResponsibleDialog, EditResponsibleDialog, ResponsibleCardDialog,
  },
  // because of typescripts circular dependency issues there is a lazy import of ItemTeachers (it opens the employee card dialog)
  // @ToDo: limit circular dept level, since there is currently no limiter for the amount of opened dialogs
  // assignedEmployees => assignedInstitutions => assignedEmployees ....
  beforeCreate() {
    if (this.$options.components) {
      // eslint-disable-next-line @typescript-eslint/no-var-requires
      this.$options.components.ItemTeachers = require('@/components/acquisitionList/ui/itemTeachers.vue').default as Component;
    }
  },
  props: {
    value: {
      type: Boolean,
      required: true,
    },
    institutionId: {
      type: Object as () => mongoose.Types.ObjectId,
      required: true,
    },
  },
  data: () => ({
    loading: false,
    institution: null as null | Institution,
    activeResponsible: null as null | Responsible,
    showEditResponsibleDialog: false,
    showResponsibleDialog: false,
    showCreateResponsibleDialog: false,
  }),
  computed: {
    contactLines(): ContactLine[] {
      return contactLinesOf(this.institution?.contactInformation);
    },
    addressText(): string {
      return addressTextOf(this.institution?.address);
    },
    mapsLink(): string {
      return mapsLinkOf(`${this.institution?.name || ''}, ${this.addressText}`);
    },
    responsibles(): Responsible[] {
      const responsibles = this.institution?.responsibles;
      return Array.isArray(responsibles) ? (responsibles as unknown[]).filter((el) => el && !isObjectId(el)) as Responsible[] : [];
    },
  },
  methods: {
    extractName,
    copy(text: string, label: string) {
      return copyWithSnackbar(this, text, label);
    },
    canReadJobType(responsible: Responsible): boolean {
      return this.$$crudAccessManager.canAccessEntity(CrudEntityTypes.RESPONSIBLE, CrudActionTypes.READ, responsible._id as any, 'jobType');
    },
    getJobTypeString(responsible: Responsible) {
      if (responsible.jobType && isJobType(responsible.jobType)) {
        switch (responsible.gender) {
          case GenderTypes.FEMALE:
            return responsible.jobType.female;
          case GenderTypes.MALE:
            return responsible.jobType.male;
          default:
            return responsible.jobType.neutral;
        }
      }
      return 'Jobposition unbekannt';
    },
    async getInstitution() {
      try {
        this.loading = true;
        this.institution = await InstitutionerApi.findById(this.institutionId, {
          populate: {
            responsibles: {
              fields: ['firstName', 'lastName', 'gender', 'jobType'],
            },
          },
        });
      } catch (e) {
        this.$emit('input', false);
        this.$$showSnackbar('Es ist ein Fehler aufgetreten.');
      } finally {
        this.loading = false;
      }
    },
    shareAccess() {
      if (this.institution) {
        this.$$shareAccessDialog.show(CrudEntityTypes.INSTITUTION, this.institution._id, 'Standort freigeben');
      }
    },
  },
  watch: {
    value: {
      immediate: true,
      handler(v: boolean) {
        if (v) {
          this.getInstitution();
        }
      },
    },
  },
});
</script>

<style scoped>
/* Field rows full width like in the profile cards */
.pcd-fields {
  margin: 4px -20px -20px;
}
</style>
<style src="@/components/acquisitionList/ui/itemDetails.css"></style>
<style src="@/components/common/profileUi/profileUi.css"></style>
